"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import { useCandidateTasks } from "@/hooks/use-candidate-tasks"
import { useCandidateComments } from "@/hooks/use-candidate-comments"
import { useCandidateFiles } from "@/hooks/use-candidate-files"
import { CommentComposer, CommentList, CommentView } from "./graphite-comments"
import { TasksView, type TaskMode } from "./graphite-tasks"
import { EditProfileView } from "./graphite-edit-profile"
import { MoveStageDialog } from "./graphite-move-stage"
import { PlacementDialog } from "../placement-dialog"
import { useCandidateRecordActions } from "@/hooks/use-candidate-record-actions"
import { useCandidateStage } from "@/hooks/use-candidate-stage"
import { asArray } from "@/lib/as-array"
import { stageHex } from "@/lib/stage-colors"
import { useCandidateProfileEdit } from "@/hooks/use-candidate-profile-edit"
import type { CandidateProfile, GetJobsJobIdCandidate200 } from "openapi/api/model"
import { CandidateOutreach } from "./candidate-outreach"
import { CandidateEventsTab, JobCandidateEventsTab } from "../candidate-events-tab"
import { CandidatePlacementTab } from "../candidate-placement-tab"
import { ResumeViewer } from "../resume-viewer"
import { TimelineViewer } from "../timeline-viewer"
import type { CommentOverlayMode, OverlayTab } from "../candidate-profile-sheet"
import {
  GraphiteCandidateProfile,
  type GraphiteCandidateProfileData,
  type ProfileComment,
  type ProfileExperience,
  type ProfileTask,
} from "./graphite-candidate-profile"

/**
 * The candidate record as canvas 6a draws it: a drawer over the list, with a
 * scrim behind and the record's own chrome inside.
 *
 * On a phone (8i) there is no room to sit beside anything, so it takes the whole
 * screen and the scrim goes away.
 */

type OverlayProfile = CandidateProfile | GetJobsJobIdCandidate200

function initialsOf(name: string) {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  )
}

function yearOf(value: unknown): string | undefined {
  if (!value) return undefined
  if (typeof value === "string") {
    const year = new Date(value).getFullYear()
    return Number.isFinite(year) ? String(year) : undefined
  }
  if (typeof value === "object" && "year" in (value as Record<string, unknown>)) {
    const year = (value as { year?: number }).year
    return year ? String(year) : undefined
  }
  return undefined
}

/**
 * Derived, not stored — so it cannot drift the way a written-down number does.
 */
/** "2014 — 2018", from whichever of the two ends the record actually has. */
function schoolYears(school: Record<string, unknown>) {
  const from = yearOf(school.starts_at)
  const to = yearOf(school.ends_at)
  if (!from && !to) return undefined
  return `${from ?? "—"} — ${to ?? "—"}`
}

function experienceSpan(experiences: ProfileExperience[]): string | undefined {
  const starts = experiences
    .map((experience) => Number(experience.starts))
    .filter((year) => Number.isFinite(year))
  if (starts.length === 0) return undefined
  const span = new Date().getFullYear() - Math.min(...starts)
  if (span < 1) return "<1 year"
  return `${span} years`
}

export function GraphiteCandidateOverlay({
  candidateProfile,
  jobId,
  jobTitle: jobTitleFromCaller,
  currentStage,
  currentTab,
  onTabChange,
  onClose,
  activeCommentMode = "none",
  activeCommentId,
  onCommentChange,
}: {
  candidateProfile: OverlayProfile
  jobId?: string | null
  /** The job's title when the profile itself does not carry one. */
  jobTitle?: string
  /** Supplied when the profile itself does not carry a stage. */
  currentStage?: string | null
  currentTab: OverlayTab
  onTabChange: (tab: OverlayTab) => void
  onClose: () => void
  activeCommentMode?: CommentOverlayMode
  activeCommentId?: string | null
  onCommentChange?: (next: {
    mode: CommentOverlayMode
    commentId?: string | null
  }) => void
}) {
  const candidateId =
    ((candidateProfile as Record<string, unknown>).candidate_id as string) ??
    ((candidateProfile as Record<string, unknown>).id as string) ??
    ""

  /*
    Tasks and comments are real, and were previously hardcoded to zero. Both
    hooks already existed for the pre-Graphite sheet; the record just never
    called them, so the rail counted nothing and the Comments row was a button
    with no handler.
  */
  /*
    Only the job-scoped profile carries the pipeline row's own id. Tasks scope
    to it server-side and the placement form writes against it; both fall back
    to resolving it from the job and candidate when it is absent.
  */
  const jobCandidateId = (candidateProfile as Record<string, unknown>)
    .job_candidate_id as string | undefined

  /*
    The job the record was opened against. 14a names it in the provenance sheet
    beside the author, so the comments hook needs it — and `data`, which is
    derived further down from the same two places, reads it from here rather
    than picking it a second time.
  */
  const jobTitle =
    (((candidateProfile as Record<string, unknown>).job_title ??
      ((candidateProfile as Record<string, unknown>).raw_json as
        | Record<string, unknown>
        | undefined
      )?.job_title) as string | undefined) ?? jobTitleFromCaller

  const [isEditingComment, setIsEditingComment] = useState(false)
  const commentsApi = useCandidateComments({
    candidateId,
    jobId,
    selectedId: activeCommentId ?? undefined,
  })
  const tasksApi = useCandidateTasks({
    candidateId,
    jobId,
    jobCandidateId,
  })

  /*
    Which of the two task screens the tab opens on. The count opens the list and
    "Add a task" opens the composer, exactly as the comments rail behaves — this
    is the intent of the click, not a place in the record, so it lives here
    rather than in the URL: a shared link should land on the record's tasks, not
    on a half-typed one.
  */
  const [taskMode, setTaskMode] = useState<TaskMode>("list")
  const openTasks = useCallback(
    (mode: TaskMode) => {
      setTaskMode(mode)
      onTabChange("tasks")
    },
    [onTabChange],
  )

  const [isMovingStage, setIsMovingStage] = useState(false)

  /*
    The record's overflow: put this candidate on another job, or in a bucket.
    Both shipped before the rebuild and neither survived it.
  */
  const actions = useCandidateRecordActions({ candidateId, isOpen: true })
  /*
    Hiring someone is the moment a placement exists, so the move hands straight
    over to the placement form — the same order the pipeline board uses: the
    stage is written first, then the placement is offered against it.
  */
  const [isPlacing, setIsPlacing] = useState(false)

  const stageApi = useCandidateStage({
    candidateId,
    jobId,
    // The job-scoped profile carries its own stage; the base one does not, so
    // the caller supplies it from the link it already resolved.
    currentStage:
      ((candidateProfile as Record<string, unknown>).stage as string | undefined) ??
      currentStage ??
      undefined,
  })

  const filesApi = useCandidateFiles(candidateId)

  const profileEdit = useCandidateProfileEdit({
    candidateId,
    jobId,
    profile: candidateProfile as Record<string, unknown>,
  })

  const profileTasks = useMemo<ProfileTask[]>(
    () =>
      tasksApi.open.slice(0, 4).map((task) => ({
        id: task.id,
        title: task.title,
        due: task.due,
        status: task.status,
        overdue: task.isLate,
      })),
    [tasksApi.open],
  )

  const profileComments = useMemo<ProfileComment[]>(
    () =>
      commentsApi.related.slice(0, 5).map((comment) => ({
        id: comment.id,
        preview: comment.preview,
        author: comment.author,
        label: comment.label,
        when: comment.when,
      })),
    [commentsApi.related],
  )

  const data = useMemo<GraphiteCandidateProfileData>(() => {
    const source = candidateProfile as Record<string, unknown>
    const raw = (source.raw_json as Record<string, unknown>) ?? {}
    const pick = <T,>(key: string): T | undefined =>
      (source[key] as T) ?? (raw[key] as T)

    const name = pick<string>("full_name") ?? "Unknown"
    const rawExperiences =
      (pick<Record<string, unknown>[]>("experiences") as
        | Record<string, unknown>[]
        | undefined) ?? []

    const experiences: ProfileExperience[] = rawExperiences.map((experience) => ({
      title: experience.title as string | undefined,
      company: experience.company as string | undefined,
      starts: yearOf(experience.starts_at),
      ends: experience.active_experience ? "now" : yearOf(experience.ends_at),
      description: experience.description as string | undefined,
    }))

    /*
      The base candidate endpoint carries no stage — only the job-scoped
      profile does — so a record opened from /candidates falls back to the
      stage the container already resolved from the job link and passes in.
      Without it `data.stage` was undefined there, and below @2xl the pill is
      the only stage there is: no stage shown, and no way into the ladder.
    */
    const stage =
      pick<string>("stage") ?? pick<string>("pipeline_stage") ?? currentStage ?? undefined

    return {
      name,
      initials: initialsOf(name),
      avatarUrl: pick<string>("profile_pic_url"),
      subtitle:
        [
          pick<string>("headline"),
          experiences[0]?.company,
          pick<string>("location") ?? pick<string>("location_str"),
        ]
          .filter(Boolean)
          .join(" · ") || "—",
      stage,
      stageColor: stage ? stageHex(stage) : undefined,
      jobTitle,
      summary: pick<string>("summary"),
      location: pick<string>("location") ?? pick<string>("location_str"),
      experienceYears: experienceSpan(experiences),
      // No home on the candidate yet. An em dash is honest; a plausible figure
      // on someone's compensation is not.
      salary: pick<string>("salary"),
      bonus: pick<string>("bonus"),
      experiences: experiences.slice(0, 3),
      allExperiences: experiences,
      totalExperiences: experiences.length,
      skills: (pick<string[]>("skills") as string[] | undefined) ?? [],
      /*
        Three things the record used to show and the rebuild left behind. Tags
        are the odd one: Edit profile has been writing them all along, with
        nowhere on the record to read them back.
      */
      tags: asArray<string>(pick("tags")),
      education: asArray<Record<string, unknown>>(pick("education")).flatMap(
        (school) => {
          const name = school.school as string | undefined
          if (!name) return []
          return [
            {
              school: name,
              detail:
                [school.degree_name, school.field_of_study]
                  .filter(Boolean)
                  .join(", ") || undefined,
              years: schoolYears(school),
            },
          ]
        },
      ),
      linkedinUrl: pick<string>("linkedin_profile_url"),
      tasks: profileTasks,
      taskCount: tasksApi.isLoading || tasksApi.isError ? undefined : tasksApi.openCount,
      comments: profileComments,
      commentCount: commentsApi.isCounted ? commentsApi.count : undefined,
      /*
        Undefined until counted, never zero — `0` told every recruiter that a
        candidate with attachments had none.

        The note here used to say there was no endpoint listing a candidate's
        files. There is: `GET /files/{owner}/{id}/presign` takes `'candidate'`
        as readily as `'job'` and answers with the list, which is what the job's
        Files tab has been reading all along. Orval names its second argument
        `fileId`, which is how that got misread.

        `bucketCount` stays out: the bucket list is keyed by a contact id, and
        `CandidateProfile` does not carry one.
      */
      fileCount: filesApi.isCounted ? filesApi.count : undefined,
      bucketCount: undefined,
    }
  }, [
    candidateProfile,
    currentStage,
    jobTitle,
    profileTasks,
    tasksApi.openCount,
    profileComments,
    commentsApi.count,
    filesApi.isCounted,
    filesApi.count,
  ])

  /*
    Comments are not one of 6a's tabs — they live in the rail — so they ride the
    separate commentMode/commentId URL state rather than `tab`. The two are
    mutually exclusive by construction in `applyOverlayAction`, so a comment
    view simply outranks whatever tab is set.
  */
  // Leaving a comment, or moving to a different one, drops edit mode.
  useEffect(() => {
    setIsEditingComment(false)
  }, [activeCommentId, activeCommentMode])

  const closeComments = () => onCommentChange?.({ mode: "none", commentId: null })

  const commentView = (() => {
    if (activeCommentMode === "none") return null

    // Editing an existing comment reuses the composer, seeded from it.
    /*
      The list, which is what the Comments count opens. Turn 20: "A row with a
      number opens the list; the action beside it opens the composer."
    */
    if (activeCommentMode === "list") {
      return (
        <CommentList
          related={commentsApi.related}
          count={commentsApi.isCounted ? commentsApi.count : undefined}
          isCounted={commentsApi.isCounted}
          isError={commentsApi.isError}
          total={commentsApi.relatedTotal}
          search={commentsApi.search}
          onSearchChange={commentsApi.setSearch}
          scope={commentsApi.scope}
          onScopeChange={commentsApi.setScope}
          canScopeToJob={commentsApi.canScopeToJob}
          onSelect={(id) => onCommentChange?.({ mode: "view", commentId: id })}
          onNew={() => onCommentChange?.({ mode: "create", commentId: null })}
          onBack={closeComments}
        />
      )
    }

    if (activeCommentMode === "create" || isEditingComment) {
      const editing = isEditingComment ? commentsApi.selected : undefined
      return (
        <CommentComposer
          /*
            The editor takes its content once, so writing a new comment and
            editing an existing one have to be different editors.
          */
          key={editing?.id ?? "new"}
          initialLabel={editing?.labelValue ?? "note"}
          initialBody={editing?.body ?? ""}
          isEditing={Boolean(editing)}
          isBusy={commentsApi.isBusy}
          onCancel={() => {
            setIsEditingComment(false)
            if (!editing) closeComments()
          }}
          onSubmit={async (label, body) => {
            const ok = editing
              ? await commentsApi.update(editing.id, label, body)
              : await commentsApi.create(label, body)
            if (!ok) return
            setIsEditingComment(false)
            if (!editing) closeComments()
          }}
        />
      )
    }

    if (!commentsApi.selected) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground">
          <p className="text-[12.5px]">
            {commentsApi.isOpening
              ? "Loading comment…"
              : commentsApi.openFailed
                ? "This comment could not be read right now."
                : "That comment is no longer available."}
          </p>
          {commentsApi.openFailed && !commentsApi.isOpening && (
            <button
              type="button"
              onClick={commentsApi.retryOpen}
              className="text-[11.5px] text-brand-ink transition-opacity hover:opacity-80"
            >
              Try again
            </button>
          )}
        </div>
      )
    }

    return (
      <CommentView
        comment={commentsApi.selected}
        related={commentsApi.related}
        count={commentsApi.count}
        scope={commentsApi.scope}
        onScopeChange={commentsApi.setScope}
        canScopeToJob={commentsApi.canScopeToJob}
        onSelect={(id) => onCommentChange?.({ mode: "view", commentId: id })}
        onEdit={() => setIsEditingComment(true)}
        onDelete={async () => {
          const ok = await commentsApi.destroy(commentsApi.selected!.id)
          if (ok) closeComments()
        }}
        onBack={closeComments}
        onOpenList={() => onCommentChange?.({ mode: "list", commentId: null })}
        isBusy={commentsApi.isBusy}
        search={commentsApi.search}
        onSearchChange={commentsApi.setSearch}
      />
    )
  })()

  const tabView = (() => {
    switch (currentTab) {
      case "resume":
        return (
          <ResumeViewer
            candidateId={candidateId}
            candidateName={data.name}
            onClose={() => onTabChange("none")}
          />
        )
      case "timeline":
        return (
          <TimelineViewer
            candidateId={candidateId}
            candidateName={data.name}
            onClose={() => onTabChange("none")}
          />
        )
      case "events":
        return jobId ? (
          <JobCandidateEventsTab
            jobId={jobId}
            candidateId={candidateId}
            candidateName={data.name}
            onClose={() => onTabChange("none")}
          />
        ) : (
          <CandidateEventsTab
            candidateId={candidateId}
            candidateName={data.name}
            onClose={() => onTabChange("none")}
          />
        )
      case "outreach":
        return (
          <CandidateOutreach
            candidate={candidateProfile}
            jobId={jobId}
            onClose={() => onTabChange("none")}
          />
        )
      case "edit":
        return (
          <EditProfileView
            draft={profileEdit.draft}
            changes={profileEdit.changes}
            isDirty={profileEdit.isDirty}
            isSaving={profileEdit.isSaving}
            onChange={profileEdit.set}
            onExperienceChange={profileEdit.setExperience}
            onSave={async () => {
              const saved = await profileEdit.save()
              if (saved) onTabChange("none")
            }}
            onDiscard={profileEdit.discard}
            onBack={() => onTabChange("none")}
          />
        )
      case "tasks":
        return (
          <TasksView
            mode={taskMode}
            onModeChange={setTaskMode}
            open={tasksApi.open}
            closed={tasksApi.closed}
            openCount={tasksApi.openCount}
            lateCount={tasksApi.lateCount}
            scope={tasksApi.scope}
            onScopeChange={tasksApi.setScope}
            canScopeToJob={tasksApi.canScopeToJob}
            onToggle={(task) =>
              task.persistedId && tasksApi.setStatus(task.persistedId, "done")
            }
            onAdd={tasksApi.create}
            onBack={() => onTabChange("none")}
            isBusy={tasksApi.isBusy}
            isLoading={tasksApi.isLoading}
            isError={tasksApi.isError}
          />
        )
      case "placements":
        return <CandidatePlacementTab candidateId={candidateId} />
      default:
        return null
    }
  })()

  const panel = (
    <GraphiteCandidateProfile
      data={data}
      /*
        A comment "closes the tabs behind it" — canvas 14a and 14b draw every
        tab unselected while one is open, because a comment is a full document
        rather than a sidebar item. Passing a tab that is not in the strip
        leaves all of them inactive without touching the tab state itself, so
        dismissing the comment returns to whichever tab was underneath.
      */
      currentTab={commentView ? ("comments" as OverlayTab) : currentTab}
      onTabChange={onTabChange}
      onClose={onClose}
      onOpenComment={(commentId) =>
        onCommentChange?.({ mode: "view", commentId })
      }
      onOpenComments={() => onCommentChange?.({ mode: "list", commentId: null })}
      onNewComment={() => onCommentChange?.({ mode: "create", commentId: null })}
      onOpenTasks={() => openTasks("list")}
      onNewTask={() => openTasks("new")}
      onMoveStage={() => setIsMovingStage(true)}
      actions={actions}
      onEdit={() => onTabChange("edit")}
      takeover={
        Boolean(commentView) || currentTab === "tasks" || currentTab === "edit"
      }
    >
      {commentView ?? tabView}
    </GraphiteCandidateProfile>
  )

  return (
    <>
      {panel}
      {/* A modal over the record, not another takeover view: 14d draws it
          centred over the whole shell, and 14g as a sheet from the bottom. */}
      <MoveStageDialog
        open={isMovingStage}
        onOpenChange={(next) => {
          setIsMovingStage(next)
          if (!next) stageApi.clearTarget()
        }}
        subtitle={[data.name, data.jobTitle].filter(Boolean).join(" · ")}
        stages={stageApi.stages}
        target={stageApi.target}
        onSelect={stageApi.setTarget}
        onMove={async () => {
          const moved = await stageApi.move()
          if (!moved) return
          setIsMovingStage(false)
          stageApi.clearTarget()
          if (moved === "hired") setIsPlacing(true)
        }}
        isMoving={stageApi.isMoving}
        canMove={stageApi.canMove}
      />

      {isPlacing && jobId ? (
        <PlacementDialog
          isOpen
          onClose={() => setIsPlacing(false)}
          jobId={jobId}
          candidateId={candidateId}
          jobCandidateId={jobCandidateId}
          candidateName={data.name}
          jobTitle={data.jobTitle}
        />
      ) : null}
    </>
  )
}
